import {
    Empty,
    Image,
    Pagination,
    Table
} from "antd";

import {
    CalendarOutlined,
    DownOutlined,
    EyeOutlined,
    ShoppingOutlined,
    UpOutlined
} from "@ant-design/icons";

import { useMemo, useState } from "react";
import dayjs from "dayjs";

import { FormatVND } from "../../utils/format";
import {
    orderStatus,
    payment
} from "../../utils/constants";

import { useDevice } from "../../hooks";


const getStatus = (status) =>
    orderStatus?.find(
        (item) =>
            String(item?.id) ===
            String(status)
    );


const getPayment = (value) =>
    payment?.find(
        (item) =>
            String(item?.value) ===
            String(value)
    )?.label || "Chưa xác định";


const getProductImage = (value) => {
    try {
        const images =
            typeof value === "string"
                ? JSON.parse(value)
                : value;

        return images
            ?.find(
                (item) =>
                    item?.type === "image"
            )
            ?.data || "";
    } catch (e) {
        return "";
    }
};


export default function TableOrder({
    order,
    pagination,
    setPagination
}) {
    const { isMobile } = useDevice();

    const [selectedStatus, setSelectedStatus] =
        useState("all");

    const [expandedOrders, setExpandedOrders] =
        useState({});


    const data = useMemo(() => {
        const source =
            Array.isArray(order?.data)
                ? order.data
                : [];

        if (selectedStatus === "all") {
            return source;
        }

        return source.filter(
            (item) =>
                String(item?.status) ===
                String(selectedStatus)
        );
    }, [
        order,
        selectedStatus
    ]);


    const tabs = useMemo(() => {
        return [
            {
                id: "all",
                data: "Tất cả"
            },
            ...(Array.isArray(orderStatus)
                ? orderStatus
                : [])
        ];
    }, []);


    const toggleOrder = (id) => {
        setExpandedOrders(
            (current) => ({
                ...current,
                [id]: !current?.[id]
            })
        );
    };


    const columns = [
        {
            title: "Mã đơn",
            dataIndex: "order_id",
            key: "order_id",
            width: 105,
            render: (value) => (
                <span
                    className="
                        font-bold
                        text-blue-600
                    "
                >
                    #{value}
                </span>
            )
        },
        {
            title: "Sản phẩm",
            dataIndex: "orderDt",
            key: "orderDt",
            render: (items) => (
                <div className="space-y-2">
                    {items?.map(
                        (item, index) => (
                            <div
                                key={
                                    item?.order_detail_id ||
                                    `${item?.product_id}-${index}`
                                }
                                className="
                                    flex
                                    items-center
                                    gap-3
                                "
                            >
                                <div
                                    className="
                                        w-12 h-12
                                        rounded-xl
                                        overflow-hidden
                                        bg-slate-50
                                        border
                                        border-slate-100
                                        shrink-0
                                    "
                                >
                                    {getProductImage(
                                        item?.product_image
                                    ) ? (
                                        <Image
                                            preview={false}
                                            src={getProductImage(
                                                item?.product_image
                                            )}
                                            width={48}
                                            height={48}
                                            loading="lazy"
                                            style={{
                                                objectFit:
                                                    "cover"
                                            }}
                                        />
                                    ) : (
                                        <div
                                            className="
                                                w-full
                                                h-full
                                                flex
                                                items-center
                                                justify-center
                                                text-slate-300
                                            "
                                        >
                                            <ShoppingOutlined />
                                        </div>
                                    )}
                                </div>

                                <div className="min-w-0">
                                    <p
                                        className="
                                            font-semibold
                                            text-slate-800
                                            text-[13px]
                                            line-clamp-2
                                            m-0
                                        "
                                    >
                                        {
                                            item?.product_name
                                        }
                                    </p>

                                    <p
                                        className="
                                            text-[12px]
                                            text-slate-400
                                            mt-1
                                            mb-0
                                        "
                                    >
                                        SL:{" "}
                                        {
                                            item?.quantity ||
                                            1
                                        }
                                    </p>
                                </div>
                            </div>
                        )
                    )}
                </div>
            )
        },
        {
            title: "Ngày đặt",
            dataIndex: "create_at",
            key: "create_at",
            width: 125,
            render: (value) => (
                <span
                    className="
                        text-[13px]
                        text-slate-600
                    "
                >
                    {dayjs(value).format(
                        "DD/MM/YYYY"
                    )}
                </span>
            )
        },
        {
            title: "Tổng tiền",
            dataIndex: "total_price",
            key: "total_price",
            width: 130,
            render: (value) => (
                <span
                    className="
                        font-bold
                        text-red-600
                        whitespace-nowrap
                    "
                >
                    {FormatVND(value)}
                </span>
            )
        },
        {
            title: "Trạng thái",
            dataIndex: "status",
            key: "status",
            width: 150,
            render: (value) => {
                const status =
                    getStatus(value);

                return (
                    <span
                        className={`
                            inline-flex
                            items-center
                            px-3 py-1.5
                            rounded-full
                            bg-slate-50
                            text-[12px]
                            font-semibold
                            ${
                                status?.color ||
                                "text-slate-600"
                            }
                        `}
                    >
                        {status?.data ||
                            "Chưa xác định"}
                    </span>
                );
            }
        }
    ];


    return (
        <div
            className="
                bg-white
                rounded-[22px]
                overflow-hidden
            "
        >
            <div
                className="
                    px-4
                    md:px-6
                    pt-5
                    md:pt-6
                "
            >
                <div
                    className="
                        flex
                        items-end
                        justify-between
                        gap-3
                    "
                >
                    <div>
                        <h2
                            className="
                                text-[19px]
                                md:text-[21px]
                                font-bold
                                text-slate-900
                                m-0
                            "
                        >
                            Đơn hàng của tôi
                        </h2>

                        <p
                            className="
                                text-[12px]
                                md:text-[13px]
                                text-slate-400
                                mt-1
                                mb-0
                            "
                        >
                            Theo dõi tình trạng
                            các đơn hàng đã đặt
                        </p>
                    </div>

                    <div
                        className="
                            hidden
                            md:flex
                            items-center
                            gap-2
                            text-[12px]
                            text-slate-400
                        "
                    >
                        <ShoppingOutlined />

                        {Number(
                            order?.total || 0
                        )}{" "}
                        đơn hàng
                    </div>
                </div>


                <div
                    className="
                        flex
                        gap-2
                        overflow-x-auto
                        mt-5
                        pb-2
                        scrollbar-hide
                    "
                >
                    {tabs.map((tab) => {
                        const active =
                            String(
                                selectedStatus
                            ) ===
                            String(tab?.id);

                        return (
                            <button
                                key={tab?.id}
                                type="button"
                                onClick={() =>
                                    setSelectedStatus(
                                        tab?.id
                                    )
                                }
                                className={`
                                    shrink-0
                                    px-4 py-2
                                    rounded-full
                                    border
                                    text-[12px]
                                    md:text-[13px]
                                    font-semibold
                                    transition-all
                                    ${
                                        active
                                            ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                                            : "bg-white text-slate-500 border-slate-200 hover:border-blue-300 hover:text-blue-600"
                                    }
                                `}
                            >
                                {tab?.data}
                            </button>
                        );
                    })}
                </div>
            </div>


            <div
                className="
                    px-3
                    md:px-6
                    pb-6
                "
            >
                {data?.length ? (
                    <>
                        {isMobile ? (
                            <div
                                className="
                                    space-y-3
                                    mt-2
                                "
                            >
                                {data.map(
                                    (item) => {
                                        const status =
                                            getStatus(
                                                item?.status
                                            );

                                        const expanded =
                                            !!expandedOrders[
                                                item?.order_id
                                            ];

                                        const firstProduct =
                                            item
                                                ?.orderDt
                                                ?.[0];

                                        return (
                                            <div
                                                key={
                                                    item?.order_id
                                                }
                                                className="
                                                    border
                                                    border-slate-100
                                                    rounded-2xl
                                                    bg-white
                                                    overflow-hidden
                                                    shadow-[0_4px_18px_rgba(15,23,42,0.04)]
                                                "
                                            >
                                                <div
                                                    className="
                                                        px-4
                                                        py-3
                                                        border-b
                                                        border-slate-100
                                                        flex
                                                        items-center
                                                        justify-between
                                                        gap-3
                                                    "
                                                >
                                                    <div>
                                                        <p
                                                            className="
                                                                text-[11px]
                                                                text-slate-400
                                                                m-0
                                                            "
                                                        >
                                                            Mã đơn
                                                        </p>

                                                        <p
                                                            className="
                                                                text-[14px]
                                                                font-bold
                                                                text-blue-600
                                                                mt-0.5
                                                                mb-0
                                                            "
                                                        >
                                                            #
                                                            {
                                                                item?.order_id
                                                            }
                                                        </p>
                                                    </div>

                                                    <span
                                                        className={`
                                                            px-3
                                                            py-1.5
                                                            rounded-full
                                                            bg-slate-50
                                                            text-[11px]
                                                            font-semibold
                                                            ${
                                                                status?.color ||
                                                                "text-slate-600"
                                                            }
                                                        `}
                                                    >
                                                        {
                                                            status?.data ||
                                                            "Chưa xác định"
                                                        }
                                                    </span>
                                                </div>


                                                <div
                                                    className="
                                                        p-4
                                                        flex
                                                        gap-3
                                                    "
                                                >
                                                    <div
                                                        className="
                                                            w-[68px]
                                                            h-[68px]
                                                            rounded-xl
                                                            overflow-hidden
                                                            bg-slate-50
                                                            border
                                                            border-slate-100
                                                            shrink-0
                                                        "
                                                    >
                                                        {getProductImage(
                                                            firstProduct?.product_image
                                                        ) ? (
                                                            <Image
                                                                preview={
                                                                    false
                                                                }
                                                                src={getProductImage(
                                                                    firstProduct?.product_image
                                                                )}
                                                                width={
                                                                    68
                                                                }
                                                                height={
                                                                    68
                                                                }
                                                                loading="lazy"
                                                                style={{
                                                                    objectFit:
                                                                        "cover"
                                                                }}
                                                            />
                                                        ) : (
                                                            <div
                                                                className="
                                                                    w-full
                                                                    h-full
                                                                    flex
                                                                    items-center
                                                                    justify-center
                                                                    text-slate-300
                                                                    text-xl
                                                                "
                                                            >
                                                                <ShoppingOutlined />
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div
                                                        className="
                                                            flex-1
                                                            min-w-0
                                                        "
                                                    >
                                                        <p
                                                            className="
                                                                text-[14px]
                                                                font-semibold
                                                                text-slate-800
                                                                line-clamp-2
                                                                m-0
                                                            "
                                                        >
                                                            {firstProduct?.product_name ||
                                                                "Đơn hàng"}
                                                        </p>

                                                        {item
                                                            ?.orderDt
                                                            ?.length >
                                                            1 && (
                                                            <p
                                                                className="
                                                                    text-[11px]
                                                                    text-slate-400
                                                                    mt-1
                                                                    mb-0
                                                                "
                                                            >
                                                                +
                                                                {item
                                                                    .orderDt
                                                                    .length -
                                                                    1}{" "}
                                                                sản phẩm
                                                                khác
                                                            </p>
                                                        )}

                                                        <div
                                                            className="
                                                                flex
                                                                items-center
                                                                gap-1.5
                                                                mt-2
                                                                text-[11px]
                                                                text-slate-400
                                                            "
                                                        >
                                                            <CalendarOutlined />

                                                            {dayjs(
                                                                item?.create_at
                                                            ).format(
                                                                "DD/MM/YYYY"
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>


                                                <div
                                                    className="
                                                        px-4
                                                        py-3
                                                        bg-slate-50/70
                                                        flex
                                                        items-center
                                                        justify-between
                                                        gap-3
                                                    "
                                                >
                                                    <div>
                                                        <p
                                                            className="
                                                                text-[11px]
                                                                text-slate-400
                                                                m-0
                                                            "
                                                        >
                                                            Tổng tiền
                                                        </p>

                                                        <p
                                                            className="
                                                                text-[16px]
                                                                font-bold
                                                                text-red-600
                                                                mt-0.5
                                                                mb-0
                                                            "
                                                        >
                                                            {FormatVND(
                                                                item?.total_price
                                                            )}
                                                        </p>
                                                    </div>

                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            toggleOrder(
                                                                item?.order_id
                                                            )
                                                        }
                                                        className="
                                                            border
                                                            border-blue-200
                                                            bg-white
                                                            text-blue-600
                                                            rounded-xl
                                                            px-3
                                                            py-2
                                                            text-[12px]
                                                            font-semibold
                                                            flex
                                                            items-center
                                                            gap-1.5
                                                        "
                                                    >
                                                        <EyeOutlined />

                                                        {expanded
                                                            ? "Thu gọn"
                                                            : "Xem chi tiết"}

                                                        {expanded ? (
                                                            <UpOutlined
                                                                className="
                                                                    text-[9px]
                                                                "
                                                            />
                                                        ) : (
                                                            <DownOutlined
                                                                className="
                                                                    text-[9px]
                                                                "
                                                            />
                                                        )}
                                                    </button>
                                                </div>


                                                {expanded && (
                                                    <div
                                                        className="
                                                            px-4
                                                            pb-4
                                                            pt-3
                                                            border-t
                                                            border-slate-100
                                                        "
                                                    >
                                                        <div
                                                            className="
                                                                space-y-3
                                                            "
                                                        >
                                                            {item?.orderDt?.map(
                                                                (
                                                                    product,
                                                                    index
                                                                ) => (
                                                                    <div
                                                                        key={
                                                                            product?.order_detail_id ||
                                                                            index
                                                                        }
                                                                        className="
                                                                            flex
                                                                            justify-between
                                                                            gap-3
                                                                            text-[12px]
                                                                        "
                                                                    >
                                                                        <span
                                                                            className="
                                                                                text-slate-600
                                                                                flex-1
                                                                            "
                                                                        >
                                                                            {
                                                                                product?.product_name
                                                                            }

                                                                            {" × "}

                                                                            {
                                                                                product?.quantity ||
                                                                                1
                                                                            }
                                                                        </span>

                                                                        <span
                                                                            className="
                                                                                font-semibold
                                                                                text-slate-800
                                                                                whitespace-nowrap
                                                                            "
                                                                        >
                                                                            {FormatVND(
                                                                                product?.price
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                )
                                                            )}
                                                        </div>

                                                        <div
                                                            className="
                                                                h-px
                                                                bg-slate-100
                                                                my-3
                                                            "
                                                        />

                                                        <div
                                                            className="
                                                                space-y-2
                                                                text-[12px]
                                                            "
                                                        >
                                                            <p
                                                                className="
                                                                    m-0
                                                                    text-slate-500
                                                                "
                                                            >
                                                                Thanh toán:{" "}
                                                                <span
                                                                    className="
                                                                        font-semibold
                                                                        text-slate-700
                                                                    "
                                                                >
                                                                    {getPayment(
                                                                        item?.payment
                                                                    )}
                                                                </span>
                                                            </p>

                                                            {item?.to_address && (
                                                                <p
                                                                    className="
                                                                        m-0
                                                                        text-slate-500
                                                                    "
                                                                >
                                                                    Địa chỉ:{" "}
                                                                    <span
                                                                        className="
                                                                            font-semibold
                                                                            text-slate-700
                                                                        "
                                                                    >
                                                                        {
                                                                            item?.to_address
                                                                        }
                                                                    </span>
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }
                                )}
                            </div>
                        ) : (
                            <div className="mt-3">
                                <Table
                                    rowKey="order_id"
                                    className="custom-table"
                                    dataSource={data}
                                    columns={columns}
                                    pagination={false}
                                    scroll={{
                                        x: 900
                                    }}
                                />
                            </div>
                        )}
                    </>
                ) : (
                    <div
                        className="
                            py-12
                            flex
                            justify-center
                        "
                    >
                        <Empty
                            description={
                                selectedStatus ===
                                "all"
                                    ? "Bạn chưa có đơn hàng"
                                    : "Không có đơn hàng ở trạng thái này"
                            }
                        />
                    </div>
                )}


                {selectedStatus === "all" &&
                    Number(order?.total || 0) >
                        0 && (
                        <div
                            className="
                                flex
                                justify-center
                                pt-6
                            "
                        >
                            <Pagination
                                current={
                                    pagination.page
                                }
                                total={
                                    order?.total
                                }
                                pageSize={
                                    pagination.pageSize
                                }
                                showSizeChanger={
                                    !isMobile
                                }
                                onChange={(
                                    page,
                                    pageSize
                                ) =>
                                    setPagination({
                                        page,
                                        pageSize
                                    })
                                }
                            />
                        </div>
                    )}
            </div>
        </div>
    );
}
